import { useEffect, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { RotateCcw, Search, CheckCircle2, XCircle, Clock, Wallet, PackageX, BarChart3, AlertTriangle, CreditCard, Settings2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { ExportMenu } from "@/components/ExportMenu";
import { TableSkeleton } from "@/components/admin/AdminSkeletons";
import { useCurrency } from "@/context/CurrencyContext";

const AUTO_APPROVE_REASONS = ["Item damaged or defective", "Wrong item received", "Item not as described", "Arrived too late", "No longer needed", "Other"];

const statusMeta = {
  requested: { cls: "bg-amber-500", label: "Pending" },
  refunded: { cls: "bg-green-600", label: "Refunded" },
  approved: { cls: "bg-blue-500", label: "Approved" },
  rejected: { cls: "bg-red-500", label: "Rejected" },
};

export const ReturnsManager = ({ onResolved }) => {
  const { format } = useCurrency();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("requested");
  const [active, setActive] = useState(null);
  const [rejectMode, setRejectMode] = useState(false);
  const [note, setNote] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [refundMethod, setRefundMethod] = useState("wallet");
  const [busy, setBusy] = useState(false);
  const [insights, setInsights] = useState(null);
  const [showInsights, setShowInsights] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkAmount, setBulkAmount] = useState("");
  const [bulkNote, setBulkNote] = useState("");
  const [remindBusy, setRemindBusy] = useState(false);

  const mediaUrl = (u) => (u && u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

  const load = useCallback(() => {
    api.get("/admin/returns").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/admin/returns/analytics").then((r) => setInsights(r.data)).catch(() => setInsights(null));
    api.get("/admin/finance/settings").then((r) => setPolicy({ return_window_days: r.data.return_window_days, credit_validity_days: r.data.credit_validity_days, auto_approve_under: r.data.auto_approve_under, auto_approve_reasons: r.data.auto_approve_reasons || [] })).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const savePolicy = async () => {
    setSavingPolicy(true);
    try {
      await api.put("/admin/finance/settings", { return_window_days: Number(policy.return_window_days), credit_validity_days: Number(policy.credit_validity_days), auto_approve_under: Number(policy.auto_approve_under), auto_approve_reasons: policy.auto_approve_reasons });
      toast.success("Returns policy saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSavingPolicy(false); }
  };

  const toggleReason = (reason) => setPolicy((p) => ({ ...p, auto_approve_reasons: p.auto_approve_reasons.includes(reason) ? p.auto_approve_reasons.filter((r) => r !== reason) : [...p.auto_approve_reasons, reason] }));

  const sendReminders = async () => {
    setRemindBusy(true);
    try {
      const { data } = await api.post("/admin/credit-expiry/send-reminders");
      toast.success(data.reminded > 0 ? `Sent ${data.reminded} expiry reminder${data.reminded !== 1 ? "s" : ""}` : "No store credit expiring in the next 14 days");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
    finally { setRemindBusy(false); }
  };

  const runBulk = async (action) => {
    if (!selected.length) return;
    const amt = parseFloat(bulkAmount);
    const hasAmt = action === "approve" && !isNaN(amt) && amt >= 0;
    const verb = action === "approve" ? (hasAmt ? `refund €${amt.toFixed(2)} each for` : "approve & refund") : "decline";
    if (!window.confirm(`Bulk ${verb} ${selected.length} return${selected.length !== 1 ? "s" : ""}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const body = { return_ids: selected, action };
      if (hasAmt) body.amount = amt;
      if (action === "reject") body.note = bulkNote;
      const { data } = await api.post("/admin/returns/bulk", body);
      toast.success(`${action === "approve" ? "Approved" : "Declined"} ${data.processed} return${data.processed !== 1 ? "s" : ""}`);
      setSelected([]); setBulkAmount(""); setBulkNote(""); load(); onResolved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk action failed"); }
    finally { setBulkBusy(false); }
  };

  const approve = async (r) => {
    const amt = parseFloat(refundAmount);
    const full = Math.round((r.amount || 0) * 100) / 100;
    const finalAmt = isNaN(amt) ? full : Math.max(0, Math.min(amt, full));
    const partial = finalAmt < full;
    const dest = refundMethod === "card" ? "the buyer's original card" : `${r.buyer_name}'s wallet`;
    if (!window.confirm(`${partial ? "Partially refund" : "Refund"} ${format(finalAmt)} to ${dest}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/returns/${r.return_id}/approve`, { amount: finalAmt, refund_method: refundMethod });
      const via = data.method === "card" ? "refunded to card" : "credited to wallet";
      toast.success(`Approved — ${format(data.credited || finalAmt)} ${via}`);
      setActive(null); load(); onResolved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to approve"); }
    finally { setBusy(false); }
  };

  const reject = async (r) => {
    setBusy(true);
    try {
      await api.post(`/admin/returns/${r.return_id}/reject`, { note });
      toast.success("Return request declined");
      setActive(null); setRejectMode(false); setNote(""); load(); onResolved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reject"); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    if (!rows) return [];
    const s = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (tab !== "all" && r.status !== tab) return false;
      if (!s) return true;
      return (r.buyer_name || "").toLowerCase().includes(s) || (r.buyer_email || "").toLowerCase().includes(s) ||
        (r.order_ref || "").toLowerCase().includes(s) || (r.reason || "").toLowerCase().includes(s);
    });
  }, [rows, q, tab]);

  const cols = [
    { header: "Order", accessor: (r) => r.order_ref },
    { header: "Buyer", accessor: (r) => r.buyer_name },
    { header: "Type", accessor: (r) => r.type },
    { header: "Reason", accessor: (r) => r.reason },
    { header: "Amount", accessor: (r) => r.amount },
    { header: "Status", accessor: (r) => r.status },
    { header: "Date", accessor: (r) => new Date(r.created_at).toLocaleDateString() },
  ];

  if (rows === null) return <TableSkeleton rows={6} cols={6} />;

  const pending = rows.filter((r) => r.status === "requested").length;
  const refunded = rows.filter((r) => r.status === "refunded");
  const rejected = rows.filter((r) => r.status === "rejected").length;
  const refundedTotal = refunded.reduce((s, r) => s + (r.refunded_amount || r.amount || 0), 0);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4" data-testid="returns-kpi-pending"><Clock className="w-5 h-5 text-amber-500 mb-2" /><p className="text-2xl font-black font-display">{pending}</p><p className="text-xs text-muted-foreground">Awaiting review</p></Card>
        <Card className="p-4" data-testid="returns-kpi-refunded"><CheckCircle2 className="w-5 h-5 text-green-600 mb-2" /><p className="text-2xl font-black font-display">{refunded.length}</p><p className="text-xs text-muted-foreground">Refunded</p></Card>
        <Card className="p-4" data-testid="returns-kpi-rejected"><XCircle className="w-5 h-5 text-red-500 mb-2" /><p className="text-2xl font-black font-display">{rejected}</p><p className="text-xs text-muted-foreground">Rejected</p></Card>
        <Card className="p-4" data-testid="returns-kpi-total"><Wallet className="w-5 h-5 text-primary mb-2" /><p className="text-2xl font-black font-display">{format(refundedTotal)}</p><p className="text-xs text-muted-foreground">Credited to wallets</p></Card>
      </div>

      <Card className="p-4 shadow-sm" data-testid="returns-insights">
        <button className="w-full flex items-center justify-between" onClick={() => setShowInsights((v) => !v)} data-testid="returns-insights-toggle">
          <span className="flex items-center gap-2 font-semibold text-sm"><BarChart3 className="w-4 h-4 text-primary" />Returns analytics</span>
          <span className="text-xs text-muted-foreground flex items-center gap-3">
            {insights && <span data-testid="return-rate">Return rate <strong className={insights.return_rate > 10 ? "text-red-500" : "text-foreground"}>{insights.return_rate}%</strong></span>}
            <span className="text-primary">{showInsights ? "Hide" : "Show"}</span>
          </span>
        </button>
        {showInsights && insights && (
          <div className="grid lg:grid-cols-2 gap-6 mt-4 pt-4 border-t border-border/50">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Top return reasons</p>
              {insights.reasons.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No data yet.</p> : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={insights.reasons} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <XAxis type="number" allowDecimals={false} fontSize={11} />
                    <YAxis type="category" dataKey="reason" width={130} fontSize={10} tickFormatter={(v) => (v.length > 20 ? v.slice(0, 19) + "…" : v)} />
                    <Tooltip cursor={{ fill: "hsl(var(--muted))" }} />
                    <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                      {insights.reasons.map((_, i) => <Cell key={i} fill="hsl(var(--primary))" fillOpacity={1 - i * 0.13} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-amber-500" />Most returned products</p>
                <div className="space-y-1.5" data-testid="top-returned-products">
                  {insights.top_products.length === 0 ? <p className="text-xs text-muted-foreground">No data yet.</p> :
                    insights.top_products.map((p, i) => (
                      <div key={i} className="flex items-center justify-between text-xs gap-2">
                        <span className="truncate">{p.name}</span>
                        <Badge variant="outline" className="shrink-0">{p.count}</Badge>
                      </div>
                    ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-amber-500" />Sellers with most returns</p>
                <div className="space-y-1.5" data-testid="top-returned-sellers">
                  {insights.top_sellers.length === 0 ? <p className="text-xs text-muted-foreground">No data yet.</p> :
                    insights.top_sellers.map((s, i) => (
                      <div key={i} className="flex items-center justify-between text-xs gap-2">
                        <span className="truncate">{s.name}</span>
                        <Badge variant="outline" className="shrink-0">{s.count}</Badge>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {policy && (
        <Card className="p-4 shadow-sm" data-testid="returns-policy-card">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2 font-semibold text-sm mr-2"><Settings2 className="w-4 h-4 text-primary" />Returns policy</div>
            <div>
              <label className="text-[11px] text-muted-foreground block">Return window (days, 0 = unlimited)</label>
              <Input type="number" min="0" max="365" value={policy.return_window_days} onChange={(e) => setPolicy((p) => ({ ...p, return_window_days: e.target.value }))} data-testid="policy-return-window" className="h-9 w-40 mt-1" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground block">Store credit validity (days, 0 = never)</label>
              <Input type="number" min="0" max="3650" value={policy.credit_validity_days} onChange={(e) => setPolicy((p) => ({ ...p, credit_validity_days: e.target.value }))} data-testid="policy-credit-validity" className="h-9 w-44 mt-1" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground block">Auto-approve returns under (€, 0 = off)</label>
              <Input type="number" min="0" step="0.01" value={policy.auto_approve_under} onChange={(e) => setPolicy((p) => ({ ...p, auto_approve_under: e.target.value }))} data-testid="policy-auto-approve" className="h-9 w-44 mt-1" />
            </div>
            <Button onClick={savePolicy} disabled={savingPolicy} className="rounded-full" data-testid="policy-save">{savingPolicy ? "Saving…" : "Save policy"}</Button>
            <Button onClick={sendReminders} disabled={remindBusy} variant="outline" className="rounded-full" data-testid="send-expiry-reminders">{remindBusy ? "Sending…" : "Send expiry reminders"}</Button>
          </div>
          <div className="mt-4">
            <label className="text-[11px] text-muted-foreground block mb-1.5">Auto-approve these reasons (any amount)</label>
            <div className="flex flex-wrap gap-2" data-testid="auto-approve-reasons">
              {AUTO_APPROVE_REASONS.map((reason) => {
                const on = policy.auto_approve_reasons?.includes(reason);
                return (
                  <button key={reason} type="button" onClick={() => toggleReason(reason)} data-testid={`auto-reason-${reason.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`text-xs rounded-full border px-3 py-1.5 transition-colors ${on ? "border-primary bg-primary text-primary-foreground font-semibold" : "border-border/60 hover:bg-muted/40"}`}>
                    {on ? "✓ " : ""}{reason}
                  </button>
                );
              })}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Returns opened after the window are auto-declined at submission. Store credit expires after the validity period. Returns at or below the auto-approve amount, or matching a selected reason above, are refunded to wallet automatically.</p>
        </Card>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="returns-tabs">
            <TabsTrigger value="requested" data-testid="returns-tab-requested">Pending{pending > 0 ? ` (${pending})` : ""}</TabsTrigger>
            <TabsTrigger value="refunded" data-testid="returns-tab-refunded">Refunded</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="returns-tab-rejected">Rejected</TabsTrigger>
            <TabsTrigger value="all" data-testid="returns-tab-all">All</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <Input data-testid="returns-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search returns…" className="pl-9 rounded-full" aria-label="Search returns" />
          </div>
          <ExportMenu filename="returns" title="Returns" columns={cols} rows={filtered} testid="returns-export" />
        </div>
      </div>

      {(() => {
        const pendingRows = filtered.filter((r) => r.status === "requested");
        const allSel = pendingRows.length > 0 && pendingRows.every((r) => selected.includes(r.return_id));
        const toggleAll = () => setSelected(allSel ? [] : pendingRows.map((r) => r.return_id));
        const toggleOne = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
        return (
          <>
            {selected.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 rounded-xl border border-primary/40 bg-primary/5 px-4 py-2.5" data-testid="bulk-action-bar">
                <span className="text-sm font-semibold whitespace-nowrap">{selected.length} selected</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">€</span>
                  <Input type="number" min="0" step="0.01" value={bulkAmount} onChange={(e) => setBulkAmount(e.target.value)} placeholder="full" data-testid="bulk-amount" className="h-8 w-24" title="Fixed refund amount per return (blank = full order total)" />
                </div>
                <Input value={bulkNote} onChange={(e) => setBulkNote(e.target.value)} placeholder="Decline reason (optional)" data-testid="bulk-note" className="h-8 w-48 hidden sm:block" />
                <div className="flex-1" />
                <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setSelected([]); setBulkAmount(""); setBulkNote(""); }} disabled={bulkBusy}>Clear</Button>
                <Button size="sm" variant="destructive" className="rounded-full" data-testid="bulk-reject" onClick={() => runBulk("reject")} disabled={bulkBusy}><XCircle className="w-4 h-4 mr-1.5" />Decline all</Button>
                <Button size="sm" className="rounded-full" data-testid="bulk-approve" onClick={() => runBulk("approve")} disabled={bulkBusy}><CheckCircle2 className="w-4 h-4 mr-1.5" />{bulkBusy ? "Processing…" : (bulkAmount ? "Refund all" : "Approve all")}</Button>
              </div>
            )}
            <Card className="p-0 overflow-hidden shadow-sm">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="w-10">{pendingRows.length > 0 && <input type="checkbox" checked={allSel} onChange={toggleAll} data-testid="bulk-select-all" className="accent-primary w-4 h-4 align-middle" aria-label="Select all pending" />}</TableHead>
                  <TableHead>Order</TableHead><TableHead>Buyer</TableHead><TableHead className="hidden md:table-cell">Reason</TableHead>
                  <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filtered.map((r) => {
                    const m = statusMeta[r.status] || { cls: "bg-slate-400", label: r.status };
                    return (
                      <TableRow key={r.return_id} data-testid={`admin-return-row-${r.return_id}`}>
                        <TableCell>{r.status === "requested" && <input type="checkbox" checked={selected.includes(r.return_id)} onChange={() => toggleOne(r.return_id)} data-testid={`bulk-select-${r.return_id}`} className="accent-primary w-4 h-4 align-middle" aria-label="Select return" />}</TableCell>
                        <TableCell><span className="font-mono text-xs">#{r.order_ref}</span></TableCell>
                        <TableCell><p className="text-sm font-medium">{r.buyer_name}</p><p className="text-[11px] text-muted-foreground truncate max-w-[160px]">{r.buyer_email}</p></TableCell>
                        <TableCell className="hidden md:table-cell text-sm"><span className="capitalize font-medium">{r.type}</span> · <span className="text-muted-foreground">{r.reason}</span>{r.auto_approved && <Badge variant="outline" className="ml-1.5 text-[10px]">auto</Badge>}</TableCell>
                        <TableCell className="text-right font-semibold text-sm">{format(r.amount || 0)}</TableCell>
                        <TableCell><Badge className={`${m.cls}`}>{m.label}</Badge></TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" className="rounded-full" data-testid={`return-view-${r.return_id}`} onClick={() => { setActive(r); setRejectMode(false); setNote(""); setRefundAmount(String(r.amount || 0)); setRefundMethod("wallet"); }}>Review</Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground text-sm"><PackageX className="w-8 h-8 mx-auto mb-2 opacity-40" />{rows.length === 0 ? "No return requests yet." : "No returns in this view."}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </Card>
          </>
        );
      })()}

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setRejectMode(false); setNote(""); } }}>
        <DialogContent className="max-w-lg" data-testid="return-review-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><RotateCcw className="w-5 h-5 text-primary" />Return · Order #{active.order_ref}</DialogTitle>
                <DialogDescription>Review the request and approve to credit the buyer's wallet, or decline it.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Buyer</span>
                  <span className="font-medium text-right">{active.buyer_name}<br /><span className="text-[11px] text-muted-foreground">{active.buyer_email}</span></span>
                </div>
                <div className="flex items-center justify-between"><span className="text-muted-foreground">Type</span><span className="capitalize font-medium">{active.type}</span></div>
                <div className="flex items-center justify-between"><span className="text-muted-foreground">Reason</span><span className="font-medium text-right max-w-[240px]">{active.reason}</span></div>
                {active.comment && <div className="rounded-lg bg-muted/50 p-3 text-muted-foreground text-xs">“{active.comment}”</div>}
                {active.photos?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1.5">Buyer photos</p>
                    <div className="flex flex-wrap gap-2" data-testid="admin-return-photos">
                      {active.photos.map((p, i) => (
                        <a key={i} href={mediaUrl(p)} target="_blank" rel="noreferrer" className="w-16 h-16 rounded-lg overflow-hidden border border-border/60 shrink-0">
                          <img src={mediaUrl(p)} alt="evidence" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between"><span className="text-muted-foreground">Order total</span><span className="font-black text-lg text-primary">{format(active.amount || 0)}</span></div>
                {active.status === "requested" && !rejectMode && (
                  <div className="rounded-lg border border-border/60 p-3">
                    <label className="text-xs text-muted-foreground flex items-center gap-1"><Wallet className="w-3.5 h-3.5" />Refund amount to credit</label>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-sm font-semibold">€</span>
                      <Input type="number" step="0.01" min="0" max={active.amount || 0} value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} data-testid="return-refund-amount" className="h-9" />
                      <Button type="button" size="sm" variant="ghost" className="text-xs shrink-0" onClick={() => setRefundAmount(String(active.amount || 0))}>Full</Button>
                    </div>
                    {parseFloat(refundAmount) < (active.amount || 0) && <p className="text-[11px] text-amber-600 mt-1">Partial refund — buyer will be credited less than the order total.</p>}
                  </div>
                )}
                {active.status === "requested" && !rejectMode && (
                  <div className="rounded-lg border border-border/60 p-3">
                    <label className="text-xs text-muted-foreground">Refund method</label>
                    <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="refund-method-select">
                      <button type="button" onClick={() => setRefundMethod("wallet")} data-testid="refund-method-wallet"
                        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${refundMethod === "wallet" ? "border-primary bg-primary/5 font-semibold" : "border-border/60 hover:bg-muted/40"}`}>
                        <Wallet className="w-4 h-4 text-primary" />Wallet credit
                      </button>
                      <button type="button" onClick={() => setRefundMethod("card")} data-testid="refund-method-card"
                        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${refundMethod === "card" ? "border-primary bg-primary/5 font-semibold" : "border-border/60 hover:bg-muted/40"}`}>
                        <CreditCard className="w-4 h-4 text-primary" />Original card
                      </button>
                    </div>
                    {refundMethod === "card" && <p className="text-[11px] text-muted-foreground mt-1.5">Refunds to the card via Stripe. Falls back to wallet credit if the order wasn't paid by card.</p>}
                  </div>
                )}
                {active.items_preview?.length > 0 && (
                  <div className="rounded-lg border border-border/60 divide-y divide-border/50">
                    {active.items_preview.map((it, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-xs">
                        <span className="truncate max-w-[240px]">{it.title} × {it.quantity}</span>
                        <span className="text-muted-foreground">{format((it.price || 0) * (it.quantity || 1))}</span>
                      </div>
                    ))}
                  </div>
                )}
                {active.status !== "requested" && (
                  <Badge className={`${(statusMeta[active.status] || {}).cls}`}>Already {(statusMeta[active.status] || {}).label}</Badge>
                )}
                {active.admin_note && <p className="text-xs text-red-500">Note: {active.admin_note}</p>}

                {rejectMode && (
                  <div>
                    <label className="text-xs text-muted-foreground">Reason for declining (shared with buyer)</label>
                    <Textarea data-testid="return-reject-note" className="mt-1" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Outside the 30-day return window" />
                  </div>
                )}
              </div>
              {active.status === "requested" && (
                <DialogFooter className="gap-2">
                  {rejectMode ? (
                    <>
                      <Button variant="outline" className="rounded-full" onClick={() => setRejectMode(false)} disabled={busy}>Back</Button>
                      <Button variant="destructive" className="rounded-full" data-testid="return-confirm-reject" onClick={() => reject(active)} disabled={busy}>{busy ? "Declining…" : "Confirm decline"}</Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" className="rounded-full" data-testid="return-reject-btn" onClick={() => setRejectMode(true)} disabled={busy}><XCircle className="w-4 h-4 mr-1.5" />Decline</Button>
                      <Button className="rounded-full" data-testid="return-approve-btn" onClick={() => approve(active)} disabled={busy}><CheckCircle2 className="w-4 h-4 mr-1.5" />{busy ? "Approving…" : (refundMethod === "card" ? "Approve & refund card" : "Approve & credit wallet")}</Button>
                    </>
                  )}
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};
